import {
  Button,
  Divider,
  Empty,
  Popconfirm,
  Tag,
  Tooltip,
  Tree,
  Typography,
  message,
} from 'antd'
import React, { useCallback, useEffect, useState } from 'react'

import {
  getTextbookByParentId,
  getTextbookConversions,
  postTextbookConversion,
} from '~/services/textbook'
import { ITextbook, ITextbookConversion } from '~/types/textbook'

/** 章节文件在课堂白板上的转换状态；没有可转换文件（如音视频、飞书链接）的章节不显示。 */
const ConversionTag: React.FC<{ conversion?: ITextbookConversion }> = ({
  conversion,
}) => {
  if (!conversion?.convertible) return null
  if (conversion.status === 'CONVERTING')
    return <Tag color="processing">转换中 {conversion.progress}%</Tag>
  if (conversion.status === 'PENDING')
    return <Tag color="processing">排队中</Tag>
  if (conversion.status === 'FAILED')
    return (
      <Tooltip
        title={[conversion.errorCode, conversion.errorMessage]
          .filter(Boolean)
          .join(' ')}
      >
        <Tag color="error">转换失败</Tag>
      </Tooltip>
    )
  return conversion.converted ? (
    <Tag color="success">已转换</Tag>
  ) : (
    <Tag>未转换</Tag>
  )
}

const Index: React.FC<{
  textbook: Partial<ITextbook>
  refreshKey?: number
  setDrawerFormData: (textbook: Partial<ITextbook>) => void
  setIsDrawerOpen: (flag: boolean) => void
}> = (props) => {
  const { setDrawerFormData, setIsDrawerOpen } = props
  const [unitList, setUnitList] = useState<ITextbook[]>([])
  useEffect(() => {
    getTextbookByParentId(props.textbook.id!)
      .then((res) => {
        setUnitList(res.data)
      })
      .catch((err) => {
        message.error(err?.response?.data)
      })
  }, [props.textbook.id])

  const [conversions, setConversions] = useState<
    Record<number, ITextbookConversion>
  >({})
  const loadConversions = useCallback(() => {
    return getTextbookConversions(props.textbook.id!)
      .then((res) => {
        setConversions(
          Object.fromEntries(res.data.map((item) => [item.textbookId, item])),
        )
      })
      .catch(() => {
        // 状态只是辅助信息，取不到时不影响章节管理。
      })
  }, [props.textbook.id])
  useEffect(() => {
    loadConversions()
  }, [loadConversions, props.refreshKey])
  const converting = Object.values(conversions).some(
    (item) => item.status === 'PENDING' || item.status === 'CONVERTING',
  )
  useEffect(() => {
    if (!converting) return
    const timer = setInterval(loadConversions, 5000)
    return () => clearInterval(timer)
  }, [converting, loadConversions])

  const convert = useCallback(
    (id: number, options: { force?: boolean; recursive?: boolean }) => {
      return postTextbookConversion(id, options)
        .then((res) => {
          if (res.data.requested > 0) {
            message.success(`已提交 ${res.data.requested} 个转换任务`)
          } else {
            message.info('没有需要转换的章节')
          }
          return loadConversions()
        })
        .catch((err) => {
          message.error(err?.response?.data || '提交转换失败')
        })
    },
    [loadConversions],
  )

  const Node = useCallback(
    (item: ITextbook) => {
      return (
        <div>
          {item.title} - {item.unit}
          {item.chapter ? ` - ${item.chapter}` : ''}
          {item.level === 2 && (
            <>
              <Typography.Link
                style={{ marginLeft: 10 }}
                onClick={() => {
                  setDrawerFormData({
                    categoryId: item.categoryId,
                    parentId: item.id,
                    author: '',
                    tags: '',
                    description: '',
                    unit: item.unit,
                    previewUrl: '',
                    onlineUrl: '',
                    downloadUrl: '',
                    order: 0,
                    level: 3,
                  })
                  setIsDrawerOpen(true)
                }}
              >
                添加章节
              </Typography.Link>
              <Divider type="vertical" />
              <Typography.Link
                onClick={() => {
                  setDrawerFormData(item)
                  setIsDrawerOpen(true)
                }}
              >
                修改
              </Typography.Link>
              <Divider type="vertical" />
              <Typography.Link
                onClick={() => convert(item.id, { recursive: true })}
              >
                转换本单元
              </Typography.Link>
            </>
          )}
          {item.level === 3 && (
            <>
              <Typography.Link
                style={{ marginLeft: 10 }}
                onClick={() => {
                  if (item.downloadUrl) {
                    window.open(item.downloadUrl)
                  } else {
                    message.error('该章节没有预览链接')
                  }
                }}
              >
                预览链接
              </Typography.Link>
              <Divider type="vertical" />
              <Typography.Link
                onClick={() => {
                  setDrawerFormData(item)
                  setIsDrawerOpen(true)
                }}
              >
                修改
              </Typography.Link>
              {conversions[item.id]?.convertible && (
                <>
                  <Divider type="vertical" />
                  <ConversionTag conversion={conversions[item.id]} />
                  {conversions[item.id].converted ? (
                    <Popconfirm
                      title="重新转换会按页再次计费，确认吗？"
                      onConfirm={() => convert(item.id, { force: true })}
                    >
                      <Typography.Link>重新转换</Typography.Link>
                    </Popconfirm>
                  ) : (
                    <Typography.Link onClick={() => convert(item.id, {})}>
                      转换
                    </Typography.Link>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )
    },
    [setDrawerFormData, setIsDrawerOpen, conversions, convert],
  )

  const loadData = useCallback((item: ITextbook) => {
    return getTextbookByParentId(item.id)
      .then((res) => {
        setUnitList((pre) => {
          const node = pre.find((p) => p.id === item.id)
          if (node) {
            node.children = (res.data || []).map((item: ITextbook) => ({
              ...item,
              isLeaf: true,
            }))
          }
          return [...pre]
        })
      })
      .catch((err) => {
        message.error(err?.response?.data)
      })
  }, [])

  return (
    <div>
      {unitList.length > 0 && (
        <Button
          size="small"
          style={{ marginBottom: 12 }}
          onClick={() => convert(props.textbook.id!, { recursive: true })}
        >
          转换全部未转换章节
        </Button>
      )}
      {unitList.length === 0 ? (
        <Empty />
      ) : (
        <Tree
          // @ts-expect-error any
          treeData={unitList}
          fieldNames={{ key: 'id' }}
          // @ts-expect-error any
          titleRender={Node}
          selectable={false}
          // @ts-expect-error any
          loadData={loadData}
        ></Tree>
      )}
    </div>
  )
}

export default Index
